/// <reference path="../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// A single nearby/search result row — Figma 3629:6819 / 3629:6927. A
// rounded-[8px] p-1 row: a small location glyph + two lines (name 14px
// #F5F6F8, address 12px #B5BAC3). Selected/hover bg #222429.

import React, { useCallback } from 'react';
import { Pressable, Text, View } from 'react-native';
import { MapPin } from 'lucide-react-native';
import type { LocationSearchResult } from './types';

export interface LocationResultRowProps {
  readonly result: LocationSearchResult;
  readonly selected: boolean;
  readonly onSelect: (result: LocationSearchResult) => void;
}

function formatDistance(meters: number | undefined): string | null {
  if (meters === undefined) {
    return null;
  }
  if (meters < 1000) {
    return `${Math.round(meters)} m`;
  }
  return `${(meters / 1000).toFixed(1)} km`;
}

export const LocationResultRow: React.FC<LocationResultRowProps> = ({
  result,
  selected,
  onSelect,
}) => {
  const distance = formatDistance(result.distanceMeters);

  const handlePress = useCallback(() => {
    onSelect(result);
  }, [onSelect, result]);

  return (
    <Pressable
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={result.label}
      accessibilityState={{ selected }}
      className={`flex-row items-center gap-2.5 rounded-[8px] p-1 ${
        selected
          ? 'bg-light-surface-secondary dark:bg-dark-surface-elevated'
          : 'bg-transparent'
      }`}
    >
      <View className="h-7 w-7 items-center justify-center rounded-[8px]">
        <MapPin size={16} color="#B5BAC3" />
      </View>
      <View className="flex-1 shrink">
        <Text
          className="text-[14px] font-medium text-[#F5F6F8]"
          numberOfLines={1}
        >
          {result.label}
        </Text>
        {result.address ? (
          <Text className="text-[12px] text-[#B5BAC3]" numberOfLines={1}>
            {result.address}
          </Text>
        ) : null}
      </View>
      {distance ? (
        <Text className="text-[12px] text-[#6B7280]">{distance}</Text>
      ) : null}
    </Pressable>
  );
};
